import api from "@/api";
import deepClone from "@/utils/deep-clone";

import { alphabetically } from "@/utils/sort-object";

const defaultLocalTeam = {
    _id: null,
    name: "",
    managers: [],
    customRightsMode: "none",
    grantUserAccessTo: [],
    grantAdminAccessTo: [],
    limitUserAccessTo: [],
    parentLocal: { _id: "" },
    settings: {},
};

const state = {
    list: [],
    details: {},
};

const getters = {
    list(state) {
        return deepClone(state.list);
    },
    listSortedByName(state) {
        return deepClone(Array.from(state.list).sort(alphabetically("name")));
    },
    listSortedHierarchically(state) {
        let list = deepClone(state.list);

        for (let i = 0; i < list.length; i++) {
            let team = list[i];
            if (team.parentLocal) {
                let parent = list.find(t => t?._id === team.parentLocal);
                if (!parent) {
                    continue;
                }

                if (!Array.isArray(parent.children)) {
                    parent.children = [];
                }
                parent.children.push(team);

                list[i] = null;
            }
        }

        return list
            .filter(Boolean)
            .filter(team => !(team.settings?.managerAutoTeam || team.settings?.nonManagerAutoTeam))
            .map(team => ({
                ...team,
                parentLocal: team.parentLocal || { _id: "" },
                children: team.children && team.children.sort(alphabetically("name")),
            }))
            .sort(alphabetically("name"));
    },
    listFilterChildrenTeams(state) {
        const list = state.list.filter(
            team =>
                !team.parentLocal &&
                !(team.settings?.managerAutoTeam || team.settings?.nonManagerAutoTeam),
        );

        return deepClone(list.sort(alphabetically("name")));
    },
    details(state) {
        return id => deepClone(state.details[id] || defaultLocalTeam);
    },
};

const actions = {
    getList({ commit, state }, { payload: { filter = "all", cache } }) {
        if (cache) {
            const list = getters.list(state);
            if (Array.isArray(list) && list.length) {
                return Promise.resolve();
            }
        }

        return api.get(`/api/teams/get/local/list/${filter}`).then(res => {
            const list = res.data || [];
            commit("updateList", { list });
        });
    },
    get({ commit }, { payload: { id } }) {
        return api.get(`/api/teams/get/local/details/${id}`).then(res => {
            const team = res.data || defaultLocalTeam;

            commit("updateDetails", {
                id,
                team: {
                    ...team,
                    parentLocal: team.parentLocal || { _id: "" },
                },
            });
        });
    },
    create({ dispatch }, { payload: { localTeam } }) {
        const data = {
            name: localTeam.name,
            type: localTeam.type,
            managers: localTeam.managers,
            members: localTeam.members,
            customRightsMode: localTeam.customRightsMode,
            customRights: localTeam.customRights,
            parentLocal: localTeam?.parentLocal?._id || null,
            isExcludedFromResultSharing: localTeam.settings?.isExcludedFromResultSharing,
        };

        return api.post("/api/teams/create", data).then(function(res) {
            const token = res?.data?.token;

            if (token) {
                dispatch("auth/updateSession", { payload: { token } }, { root: true });
            }

            return dispatch(
                "teams/local/getList",
                { payload: { filter: "allStacked" } },
                { root: true },
            );
        });
    },
    update({ dispatch }, { payload: { localTeam } }) {
        const data = {
            name: localTeam.name,
            managers: localTeam.managers,
            customRightsMode: localTeam.customRightsMode,
            grantUserAccessTo: localTeam.grantUserAccessTo,
            grantAdminAccessTo: localTeam.grantAdminAccessTo,
            limitUserAccessTo: localTeam.limitUserAccessTo,
            parentLocal: localTeam?.parentLocal?._id || null,
            isExcludedFromResultSharing: localTeam.settings?.isExcludedFromResultSharing,
        };

        return api.post(`/api/teams/update/local/${localTeam._id}`, data).then(res => {
            const token = res?.data?.token;

            if (token) {
                dispatch("auth/updateSession", { payload: { token } }, { root: true });
            }

            return dispatch(
                "teams/local/getList",
                { payload: { filter: "allStacked" } },
                { root: true },
            );
        });
    },
    delete({ commit, dispatch }, { payload: { id } }) {
        return api.post(`/api/teams/delete/local/${id}`, {}).then(() => {
            commit("updateDetails", { id, team: null });
            return dispatch(
                "teams/local/getList",
                { payload: { filter: "allStacked" } },
                { root: true },
            );
        });
    },

    addUser({ dispatch }, { payload: { teamId, userId, role } }) {
        return api
            .post(`/api/teams/update/${teamId}/members/add`, { user: userId, role })
            .then(res => {
                const token = res?.data?.token;

                if (token) {
                    dispatch("auth/updateSession", { payload: { token } }, { root: true });
                }
            });
    },
    removeUser({ dispatch }, { payload: { teamId, userId } }) {
        return api
            .post(`/api/teams/update/${teamId}/members/remove`, { user: userId })
            .then(res => {
                const token = res?.data?.token;

                if (token) {
                    dispatch("auth/updateSession", { payload: { token } }, { root: true });
                }
            });
    },

    reset({ commit }) {
        commit("updateList", { list: [] });
    },
};

const mutations = {
    updateList(state, { list }) {
        state.list = [...list];
    },
    updateDetails(state, { id, team }) {
        state.details[id] = team;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
